import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { useApiErrorHandler } from '@/account/hook/useApiErrorHandler';
import {
  loadTableViews,
  getTableView,
  createTableView,
  updateTableView,
  deleteTableView,
  duplicateTableView,
  setDefaultTableView,
  tableViewsQueryKey,
  type CreateTableViewParams,
  type UpdateTableViewParams
} from './tableViews';
import { useGlobalToast } from '@/lib/hook';

export const useTableViews = (tableIdentifier?: string, tenantId?: number) => {
  const { createTenantAdminApiRequest } = useTenantApi();
  const { reportToGlobal } = useApiErrorHandler();

  return useQuery({
    queryKey: [tableViewsQueryKey, tableIdentifier, tenantId],
    queryFn: async () => {
      try {
        const response = await loadTableViews(createTenantAdminApiRequest)(tableIdentifier, tenantId);
        return response.items || [];
      } catch (error) {
        reportToGlobal(error as Error);
        throw error;
      }
    }
  });
};

export const useTableView = (id: number | null) => {
  const { createTenantAdminApiRequest } = useTenantApi();
  const { reportToGlobal } = useApiErrorHandler();

  return useQuery({
    queryKey: [tableViewsQueryKey, id],
    queryFn: async () => {
      if (!id) return null;
      try {
        const response = await getTableView(createTenantAdminApiRequest)(id);
        return (response as any).item;
      } catch (error) {
        reportToGlobal(error as Error);
        throw error;
      }
    },
    enabled: !!id,
  });
};

export const useCreateTableView = () => {
  const { createTenantAdminApiRequest } = useTenantApi();
  const { reportToGlobal } = useApiErrorHandler();
  const { showToast } = useGlobalToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CreateTableViewParams) => {
      const response = await createTableView(createTenantAdminApiRequest)(params);
      return (response as any).item;
    },
    onSuccess: (data) => {
      showToast.success(`View "${data.name}" created successfully`);
      queryClient.invalidateQueries({ queryKey: [tableViewsQueryKey] });
    },
    onError: (error: Error) => {
      reportToGlobal(error);
      showToast.error('Failed to create view');
    },
  });
};

export const useUpdateTableView = () => {
  const { createTenantAdminApiRequest } = useTenantApi();
  const { reportToGlobal } = useApiErrorHandler();
  const { showToast } = useGlobalToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, params }: { id: number, params: UpdateTableViewParams }) => {
      const response = await updateTableView(createTenantAdminApiRequest)(id, params);
      return (response as any).item;
    },
    onSuccess: (data) => {
      showToast.success(`View "${data.name}" updated successfully`);
      queryClient.invalidateQueries({ queryKey: [tableViewsQueryKey] });
    },
    onError: (error: Error) => {
      reportToGlobal(error);
      showToast.error('Failed to update view');
    },
  });
};

export const useDeleteTableView = () => {
  const { createTenantAdminApiRequest } = useTenantApi();
  const { reportToGlobal } = useApiErrorHandler();
  const { showToast } = useGlobalToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      return await deleteTableView(createTenantAdminApiRequest)(id);
    },
    onSuccess: () => {
      showToast.success('View deleted successfully');
      queryClient.invalidateQueries({ queryKey: [tableViewsQueryKey] });
    },
    onError: (error: Error) => {
      reportToGlobal(error);
      showToast.error('Failed to delete view');
    },
  });
};

export const useDuplicateTableView = () => {
  const { createTenantAdminApiRequest } = useTenantApi();
  const { reportToGlobal } = useApiErrorHandler();
  const { showToast } = useGlobalToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      name,
      scope
    }: {
      id: number
      name: string
      scope?: 'personal' | 'tenant'
    }) => {
      const response = await duplicateTableView(createTenantAdminApiRequest)(id, name, scope);
      return (response as any).item;
    },
    onSuccess: (data) => {
      showToast.success(`View "${data.name}" created successfully`);
      queryClient.invalidateQueries({ queryKey: [tableViewsQueryKey] });
    },
    onError: (error: Error) => {
      reportToGlobal(error);
      showToast.error('Failed to duplicate view');
    },
  });
};

export const useSetDefaultTableView = () => {
  const { createTenantAdminApiRequest } = useTenantApi();
  const { reportToGlobal } = useApiErrorHandler();
  const { showToast } = useGlobalToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await setDefaultTableView(createTenantAdminApiRequest)(id);
      return (response as any).item;
    },
    onSuccess: (data) => {
      showToast.success(`"${data.name}" is now your default view`);
      queryClient.invalidateQueries({ queryKey: [tableViewsQueryKey] });
    },
    onError: (error: Error) => {
      reportToGlobal(error);
      showToast.error('Failed to set default view');
    },
  });
};
